// Card do convite da Gestão da equipe de pista (Meta de Frentista) na Home (feature 161, spec seção 8).
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, IconButton, Typography } from '@mui/material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CloseIcon from '@mui/icons-material/Close';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { __t } from '../../i18n/t';
import { OfferApiError, OfferEndedReason, OfferStatus, OfferView } from '../../models/attendantGoalOffer';
import { dismissOffer, getOffer, requestContinue } from '../../services/attendantGoalOfferApi';
import { COLORS, cardSx } from '../../resources/dashboards/shared';
import { ATTENDANT_GOAL_TRIAL_ROUTE, fixedHeadline, insightKindFor, offerErrorText } from './offerCopy';
import OfferPitch from './OfferPitch';
import { useOfferGate } from './useOfferGate';
import { useOfferInsight } from './useOfferInsight';

// Peça do plano -imagem (pontuax-motion/reels/05-painel-home), versionada no nome por causa do cache do CloudFront.
// v3: quadro de exemplo com selo "EXEMPLO" e nomes genéricos (sobe para o S3 antes do deploy desta tela).
export const ATTENDANT_GOAL_OFFER_IMAGE = 'https://s3.pontuax.com.br/marketing/motion/PontuaX_home_meta-frentista_v3.jpg';
const BADGE_GRADIENT = 'linear-gradient(135deg, #6366f1, #a855f7)';
// Convite pendente abre em dialog na Home uma vez por dia por navegador (o card continua lá para quem fechou).
const DIALOG_SEEN_KEY = 'attendant-goal-offer-dialog';

const AttendantGoalOfferCard = () => {
  const { allowed } = useOfferGate();
  const navigate = useNavigate();
  const notify = useNotify();
  const [offer, setOffer] = useState<OfferView | null>(null);
  const [busy, setBusy] = useState(false);
  const [imageOk, setImageOk] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { content } = useOfferInsight(offer?.id, offer ? insightKindFor(offer.status) : undefined);

  // Erro (inclusive síncrono, como chain_missing) nunca quebra a Home: o card some em silêncio.
  const reload = useCallback(async () => {
    try {
      setOffer(await getOffer());
    } catch {
      setOffer(null);
    }
  }, []);
  useEffect(() => { if (allowed) reload(); }, [allowed, reload]);

  const pendingId = offer?.status === OfferStatus.pending ? offer.id : null;
  useEffect(() => {
    if (!pendingId) { setDialogOpen(false); return; }
    const mark = `${pendingId}:${new Date().toISOString().slice(0, 10)}`;
    try {
      if (localStorage.getItem(DIALOG_SEEN_KEY) === mark) return;
      localStorage.setItem(DIALOG_SEEN_KEY, mark);
    } catch { /* sem storage (aba anônima): abre mesmo assim */ }
    setDialogOpen(true);
  }, [pendingId]);

  if (!allowed || !offer) return null;
  if (offer.status === OfferStatus.ended && offer.endedReason === OfferEndedReason.converted) return null;

  const act = async (call: () => Promise<OfferView | null>) => {
    if (busy) return;
    setBusy(true);
    try {
      setOffer(await call());
    } catch (err) {
      notify(offerErrorText((err as OfferApiError).code ?? null), { type: 'error' });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const goToTrial = () => navigate(ATTENDANT_GOAL_TRIAL_ROUTE);
  const headline = content?.headline || fixedHeadline(offer.status);

  if (offer.status === OfferStatus.pending) {
    const pitch = (variant: 'dialog' | 'card') => (
      <OfferPitch
        offer={offer}
        headline={headline}
        image={imageOk ? ATTENDANT_GOAL_OFFER_IMAGE : null}
        onImageError={() => setImageOk(false)}
        busy={busy}
        onSee={goToTrial}
        onDismiss={() => act(() => dismissOffer(offer.id))}
        variant={variant}
      />
    );
    return (
      <>
        {pitch('card')}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: {
            borderRadius: 5, overflow: 'hidden', m: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: undefined },
            maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
          } }}
        >
          <IconButton
            aria-label={__t('Fechar')}
            onClick={() => setDialogOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12, zIndex: 2, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', '&:hover': { bgcolor: '#fff' } }}
          >
            <CloseIcon />
          </IconButton>
          {/* Rolagem e barra do CTA ficam dentro do OfferPitch: o pé do dialog nunca sai da tela. */}
          {pitch('dialog')}
        </Dialog>
      </>
    );
  }

  const isActive = offer.status === OfferStatus.active;
  const title = isActive
    ? (offer.daysLeft === 1 ? __t('Degustação: falta 1 dia') : __t('Degustação: faltam %{n} dias', { n: offer.daysLeft ?? 0 }))
    : __t('Degustação encerrada');

  return (
    <Box sx={{ ...cardSx, mt: 3, maxWidth: 920, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {imageOk && (
        <Box
          component="img"
          src={ATTENDANT_GOAL_OFFER_IMAGE}
          alt=""
          onError={() => setImageOk(false)}
          sx={{ width: '100%', aspectRatio: '2400 / 1104', objectFit: 'cover', borderRadius: 3, display: 'block' }}
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 22, height: 22, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BADGE_GRADIENT }}>
            <LeaderboardIcon sx={{ fontSize: 13, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: COLORS.brand }}>{title}</Typography>
        </Box>

        {/* Texto da IA sempre como texto puro, nunca dangerouslySetInnerHTML. */}
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: COLORS.ink }}>{headline}</Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="contained" disabled={busy} onClick={goToTrial}>
            {isActive ? 'Ver resultados' : 'Ver resultado final'}
          </Button>
          {offer.continueRequestedAt
            ? <Chip color="success" variant="outlined" label="Recebemos seu pedido" />
            : offer.canContinue && (
              <Button variant="outlined" disabled={busy} onClick={() => act(() => requestContinue(offer.id))}>
                Quero continuar com desconto
              </Button>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default AttendantGoalOfferCard;
